import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  CalendarCheck,
  HeartPulse,
  Leaf,
  MapPin,
  Sparkles,
  Users,
} from "lucide-react";
import SimpleSlider from "./Slick";
import Testimonials from "../pages/Testimonial";
import StatsSection from "../pages/StatsSection";
import Footer from "../pages/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0 },
};

const featureCards = [
  {
    title: "Personal attention",
    text: "Choose classes that match your level, schedule, goals, and preferred location.",
    icon: Users,
  },
  {
    title: "Therapy-led practice",
    text: "Sessions focus on strength, breath, posture, mobility, and long-term wellness.",
    icon: HeartPulse,
  },
  {
    title: "Flexible booking",
    text: "Book daily sessions, demos, subscriptions, and events from one polished flow.",
    icon: CalendarCheck,
  },
];

const programs = [
  "Hatha Yoga",
  "Power Yoga",
  "Therapeutic Yoga",
  "Breathwork",
  "Meditation",
  "Stress Relief",
];

const CustomerLayout = () => {
  const navigate = useNavigate();

  return (
    <main className="ym-page">
      <section className="ym-hero">
        <div className="ym-hero-media">
          <img src="/geropage02.jpeg" alt="Yoga class at YogaMatic" />
        </div>

        <div className="ym-hero-content">
          <motion.div
            className="ym-hero-panel"
            initial="hidden"
            animate="show"
            variants={fadeUp}
            transition={{ duration: 0.65, ease: "easeOut" }}
          >
            <span className="ym-eyebrow">
              <Leaf size={14} />
              Jaipur yoga studio
            </span>
            <h1 className="ym-title">YogaMatic</h1>
            <p className="ym-lead">
              Practice with experienced guidance, flexible class booking, and wellness experiences designed for real life.
            </p>
            <div className="ym-actions" style={{ marginTop: 28 }}>
              <button className="ym-button ym-button-gold" onClick={() => navigate("/Get_Start")}>
                Explore Services
                <ArrowRight size={18} />
              </button>
              <button className="ym-button ym-button-ghost" onClick={() => navigate("/demo-services")}>
                Book a Free Demo
              </button>
            </div>
          </motion.div>

          <motion.div
            className="ym-hero-highlights"
            initial="hidden"
            animate="show"
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.1, delayChildren: 0.25 } },
            }}
          >
            {[
              ["2", "Yoga centers"],
              ["10+", "Certified trainers"],
              ["250+", "Happy customers"],
            ].map(([value, label]) => (
              <motion.div className="ym-hero-highlight" variants={fadeUp} key={label}>
                <strong>{value}</strong>
                <span>{label}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <StatsSection />

      <section className="ym-section">
        <div className="ym-container ym-split">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.35 }} variants={fadeUp}>
            <span className="ym-eyebrow">
              <Sparkles size={14} />
              Why YogaMatic
            </span>
            <h2 className="ym-title-small">A calmer way to build strength, flexibility, and focus.</h2>
            <p className="ym-lead">
              YogaMatic blends traditional yoga discipline with simple digital booking, clear schedules, and warm guidance
              for beginners and regular practitioners.
            </p>

            <div className="ym-grid-3" style={{ marginTop: 28 }}>
              {featureCards.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.article
                    className="ym-card ym-card-pad"
                    key={feature.title}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ delay: index * 0.08 }}
                    whileHover={{ y: -6 }}
                  >
                    <span className="ym-icon-box">
                      <Icon size={22} />
                    </span>
                    <h3 style={{ margin: "18px 0 8px", fontSize: 20, fontWeight: 850 }}>{feature.title}</h3>
                    <p className="ym-text">{feature.text}</p>
                  </motion.article>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            className="ym-image-stack"
            initial={{ opacity: 0, x: 28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.55 }}
          >
            <img src="/yoga05.jpeg" alt="YogaMatic breathing practice" />
            <img src="/yoga13.jpeg" alt="YogaMatic studio practice" />
          </motion.div>
        </div>
      </section>

      <section className="ym-section ym-section-band">
        <div className="ym-container">
          <div style={{ display: "flex", justifyContent: "space-between", gap: 24, alignItems: "end", flexWrap: "wrap" }}>
            <div>
              <span className="ym-eyebrow">
                <BadgeCheck size={14} />
                Programs
              </span>
              <h2 className="ym-title-small">Find the practice that fits your body today.</h2>
            </div>
            <button className="ym-button ym-button-primary" onClick={() => navigate("/Get_Start")}>
              View All Classes
              <ArrowRight size={18} />
            </button>
          </div>

          <div className="ym-grid-3" style={{ marginTop: 30 }}>
            {programs.map((program, index) => (
              <motion.div
                key={program}
                className="ym-card ym-card-pad"
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ y: -5 }}
              >
                <div className="ym-meta-row">
                  <span className="ym-icon-box">
                    <Leaf size={20} />
                  </span>
                  <h3 style={{ margin: 0, fontSize: 20, fontWeight: 850 }}>{program}</h3>
                </div>
                <p className="ym-text" style={{ marginTop: 14 }}>
                  Guided sessions with mindful sequencing, clear instruction, and room to progress at your pace.
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="ym-section">
        <div className="ym-wide">
          <div className="ym-container" style={{ textAlign: "center" }}>
            <span className="ym-eyebrow" style={{ marginInline: "auto" }}>
              <Sparkles size={14} />
              Gallery
            </span>
            <h2 className="ym-title-small">Inside the YogaMatic practice.</h2>
            <p className="ym-lead" style={{ marginInline: "auto" }}>
              Real glimpses from classes, workshops, and the community that keeps showing up.
            </p>
          </div>
          <SimpleSlider />
        </div>
      </section>

      <section className="ym-section-tight">
        <div className="ym-container">
          <motion.div
            className="ym-card ym-glass ym-cta-panel"
            style={{
              color: "#fff",
            }}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
          >
            <div>
              <span className="ym-eyebrow" style={{ background: "rgba(255,255,255,0.14)", color: "#fff" }}>
                <MapPin size={14} />
                Sanganer and Mansarovar
              </span>
              <h2 className="ym-title-small" style={{ color: "#fff" }}>Ready to reserve your mat?</h2>
              <p className="ym-lead" style={{ color: "rgba(255,255,255,0.78)" }}>
                Choose your service, location, date, slot, and complete booking in a few steps.
              </p>
            </div>
            <button className="ym-button ym-button-gold" onClick={() => navigate("/Get_Start")}>
              Book a Class
              <ArrowRight size={18} />
            </button>
          </motion.div>
        </div>
      </section>

      <Testimonials />
      <Footer />
    </main>
  );
};

export default CustomerLayout;
